import React, { useState } from "react";
import {
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Container } from "../../../globalstyled";
import AddressStep from "./steps/AddressStep";
import {
  useCreateAccount,
  useCreateLead,
} from "../../../apolloClient/hooks/itemCreation/createHooks";
import { toast } from "react-toastify";
import {
  CreateCustomerInput,
  CreateLeadInput,
} from "../../../types/graphQlQuery.interface";
import { useFetchProductSytems } from "../../../apolloClient/hooks/general/fetchHook";
import { useNavigate } from "react-router-dom";
import { useAccess } from "../../../hooks/useAccess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { logEvent, PROJECT_EVENTS } from "../../../utils/amplitude";

const steps = [
  "Address Input",
  "Select Product System",
  "Select Payment Mode",
  "User Details",
];

enum Utm {
  SELF = "self",
  REFERRAL = "referral",
  WAITLIST = "waitlist",
  SALES = "sales",
}

export enum PayType {
  INSURANCE = "INSURANCE",
  CASH = "CASH",
}

const VALID_STATES = ["UT", "TX", "NE", "IA"];

const CreateProject: React.FC = () => {
  // Use the global hook for access permissions
  const { loading: accessLoading } = useAccess();

  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [creationStartTime] = useState(Date.now());

  // Track project creation started
  React.useEffect(() => {
    logEvent(PROJECT_EVENTS.CREATION_STARTED, { source: 'create_button' });
  }, []);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]); // Track selected products
  const [selectedPaymentMode, setSelectedPaymentMode] =
    useState<PayType | null>(null);
  const [userDetails, setUserDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobilePhone: "",
  });

  const placeId = localStorage.getItem("place_id");

  const { CreateLead } = useCreateLead();
  const { createAccount } = useCreateAccount();
  const { error, loading, productSystems, refetch } = useFetchProductSytems();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleProductSelection = () => {
    if (selectedProducts.length === 0) {
      toast("Please select at least one product system.");
      return false;
    }
    localStorage.setItem("selectedProducts", JSON.stringify(selectedProducts));
    
    // Track product selection
    logEvent(PROJECT_EVENTS.PRODUCT_SELECTED, { 
      product_ids: selectedProducts, 
      product_count: selectedProducts.length 
    });
    
    return true;
  };

  const handlePaymentModeSelection = () => {
    if (!selectedPaymentMode) {
      toast("Please select a payment mode.");
      return false;
    }
    localStorage.setItem("selectedPaymentMode", selectedPaymentMode);
    
    // Track payment mode selection
    logEvent(PROJECT_EVENTS.PAYMENT_MODE_SELECTED, { 
      payment_mode: selectedPaymentMode 
    });
    
    return true;
  };

  function parseCityAndState(address: any) {
    const regex = /^([\w\s]+?),\s*([\w\s]+?),\s*([\w\s]+)$/;
    const matches = address.match(regex);
    return matches
      ? { city: matches[1].trim(), state: matches[2].trim() }
      : null;
  }

  const handleAddressValidation = async () => {
    if (!selectedAddress) {
      toast("Please select an Address first");
      return false;
    }

    const cityAndState = parseCityAndState(
      selectedAddress.structured_formatting.secondary_text
    );

    const sanitizedState = cityAndState?.state.replace(/[^a-zA-Z]/g, "");

    if (!cityAndState || !VALID_STATES.includes(sanitizedState)) {
      alert("Currently, we cannot move forward with this location. Thanks!");
      return false;
    }

    const lead: CreateLeadInput = {
      streetAddress: selectedAddress.structured_formatting.main_text,
      unit: "",
      city: cityAndState.city,
      zipCode: "10257", // Placeholder for zip code
      state: sanitizedState,
      utm: Utm.SALES,
    };
    
    try {
      const data = await CreateLead(lead);
      if (data) {
        localStorage.setItem("place_id", selectedAddress.place_id);
        localStorage.setItem("lead", data);
        
        // Track address selection
        logEvent(PROJECT_EVENTS.ADDRESS_SELECTED, { 
          state: sanitizedState, 
          is_valid: true 
        });
        
        return true;
      }
    } catch (error) {
      toast("Error selecting your address. Please try again!");
    }

    return false;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const isValid = await handleAddressValidation();
      if (!isValid) return;
    } else if (activeStep === 1) {
      const isValid = handleProductSelection();
      if (!isValid) return;
    } else if (activeStep === 2) {
      const isValid = handlePaymentModeSelection();
      if (!isValid) return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => setActiveStep((prevStep) => prevStep - 1);

  const handleUserDetailsChange = (field: string, value: string | boolean) => {
    setUserDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleUserDetailsValidation = () => {
    const {
      firstName,
      lastName,
      email,
      mobilePhone,
    } = userDetails;
    if (
      !firstName ||
      !lastName ||
      !email ||
      !mobilePhone
    ) {
      toast("Please fill in all fields.");
      return false;
    }
    return true;
  };

  const handleFinish = async () => {
    const isValid = handleUserDetailsValidation();
    if (!isValid) return;

    try {
      const theId = localStorage.getItem("lead");
      const selectedSystems = JSON.parse(
        localStorage.getItem("selectedProducts") || "[]"
      );
      const selectedPayType = localStorage.getItem("selectedPaymentMode");

      const input: CreateCustomerInput = {
        email: userDetails.email,
        phone: userDetails.mobilePhone,
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        leadId: theId,
        isAdmin: true,
        placeId: placeId || "",
        project: {
          productIds: selectedSystems,
          paymentType: selectedPayType as PayType,
        },
      };

      const createAccountResponse = await createAccount(input);

      if (createAccountResponse) {
        toast.success("Account created successfully!");
        
        // Track project creation success
        logEvent(PROJECT_EVENTS.PROJECT_CREATED, {
          project_id: createAccountResponse?.projectId || 'unknown',
          customer_id: 'unknown',
          product_systems: selectedSystems,
          payment_type: selectedPayType as string
        });
        
        localStorage.removeItem("lead");
        localStorage.removeItem("selectedProducts");
        localStorage.removeItem("selectedPaymentMode");
        navigate("/projects");
        setSelectedAddress(null);
        setSelectedProducts([]);
        setSelectedPaymentMode(null);
        setUserDetails({
          firstName: "",
          lastName: "",
          email: "",
          mobilePhone: "",
        });
      }
    } catch (error) {
      toast.error("Failed to create account. Please try again.");
    }
  };

  const toggleProductSelection = (id: number) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((productId) => productId !== id)
        : [...prevSelected, id]
    );
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <AddressStep
            initialAddress={selectedAddress}
            onAddressSelect={(address) => setSelectedAddress(address)}
          />
        );
      case 1:
        return (
          <div>
            <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
              Select Product Systems
            </h2>
            <p
              className="text-wrap"
              style={{
                textAlign: "center",
                marginBottom: "20px",
                color: "#555",
              }}
            >
              Choose the product systems you want to include in your project.
              Scroll to see more options.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "20px",
                maxHeight: "400px",
                overflowY: "auto",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              {Array.isArray(productSystems) &&
                productSystems
                  .filter((product) => !product.archived)
                  .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
                  .map((product) => (
                    <div
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      style={{
                        border: selectedProducts.includes(product.id)
                          ? "2px solid #007BFF"
                          : "1px solid #ccc",
                        borderRadius: "8px",
                        padding: "10px",
                        width: isSmallScreen ? "90%" : "200px",
                        minWidth: isSmallScreen ? "90%" : "200px",
                        maxWidth: isSmallScreen ? "90%" : "200px",
                        textAlign: "center",
                        backgroundColor: selectedProducts.includes(product.id)
                          ? "#E6F0FF"
                          : "#fff",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        margin: isSmallScreen ? "0 auto" : undefined,
                      }}
                    >
                      <img
                        src={product.thumbnail}
                        alt={product.name}
                        style={{
                          width: "100%",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          marginBottom: "10px",
                        }}
                      />
                      <h4
                        style={{
                          margin: "10px 0",
                          fontSize: "16px",
                          color: "#333",
                        }}
                      >
                        {product.name}
                      </h4>
                      <p style={{ fontSize: "14px", color: "#777" }}>
                        {product.duration}
                      </p>
                    </div>
                  ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
              Select Payment Mode
            </h2>
            <p
              className="text-wrap"
              style={{
                textAlign: "center",
                marginBottom: "20px",
                color: "#555",
              }}
            >
              Tell us how you plan to pay. We help you facilitate your payment
              options at a later stage during your project.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "20px",
                maxHeight: "500px",
                overflowY: "auto",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              {[
                {
                  id: PayType.INSURANCE,
                  label: "Insurance proceeds",
                  icon: "🏠",
                },
                { id: PayType.CASH, label: "Cash payment", icon: "💵" },
              ].map((option) => (
                <div
                  key={option.id}
                  onClick={() => setSelectedPaymentMode(option.id)}
                  style={{
                    border:
                      selectedPaymentMode === option.id
                        ? "2px solid #007BFF"
                        : "1px solid #ccc",
                    borderRadius: "8px",
                    padding: "10px",
                    width: isSmallScreen ? "90%" : "200px",
                    minWidth: isSmallScreen ? "90%" : "200px",
                    maxWidth: isSmallScreen ? "90%" : "200px",
                    textAlign: "center",
                    backgroundColor:
                      selectedPaymentMode === option.id ? "#E6F0FF" : "#fff",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    margin: isSmallScreen ? "0 auto" : undefined,
                  }}
                >
                  <div style={{ fontSize: "40px", marginBottom: "10px" }}>
                    {option.icon}
                  </div>
                  <h4
                    style={{
                      margin: "10px 0",
                      fontSize: "16px",
                      color: "#333",
                    }}
                  >
                    {option.label}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <h2
              style={{
                textAlign: "center",
                marginBottom: "20px",
                fontSize: "24px",
                color: "#333",
              }}
            >
              Let's create your account
            </h2>
            <p
              style={{
                textAlign: "center",
                marginBottom: "20px",
                color: "#555",
                fontSize: "16px",
              }}
              className="text-wrap"
            >
              The last step to get your project started is to create an account.
              So tell us a little bit about yourself!
            </p>
            <form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                maxWidth: "400px",
                margin: "0 auto",
              }}
            >
              <input
                type="text"
                placeholder="First name"
                value={userDetails.firstName}
                onChange={(e) =>
                  handleUserDetailsChange("firstName", e.target.value)
                }
                style={{
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                }}
              />
              <input
                type="text"
                placeholder="Last name"
                value={userDetails.lastName}
                onChange={(e) =>
                  handleUserDetailsChange("lastName", e.target.value)
                }
                style={{
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                }}
              />
              <input
                type="email"
                placeholder="Email"
                value={userDetails.email}
                onChange={(e) =>
                  handleUserDetailsChange("email", e.target.value)
                }
                style={{
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                }}
              />
              <input
                type="text"
                placeholder="Mobile phone"
                value={userDetails.mobilePhone}
                onChange={(e) =>
                  handleUserDetailsChange("mobilePhone", e.target.value)
                }
                style={{
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                }}
              />
            </form>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Button
        onClick={() => navigate("/projects")}
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        style={{
          margin: "10px 20px",
          textTransform: "none",
          fontSize: "16px",
          color: "#007BFF",
          borderColor: "#007BFF",
        }}
      >
        Back to projects
      </Button>
      <Container
        style={{
          margin: "10px auto",
          padding: "20px",
          minWidth: "90%",
          backgroundColor: "#f9f9f9",
          borderRadius: "10px",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            marginBottom: "20px",
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isSmallScreen}
            orientation={isSmallScreen ? "vertical" : "horizontal"}
            sx={{
              minWidth: isSmallScreen ? "100%" : 0,
              width: isSmallScreen ? "100%" : "auto",
            }}
          >
            {steps.map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Box sx={{ marginBottom: "20px", width: "100%" }}>{renderStepContent(activeStep)}</Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button variant="contained" color="primary" onClick={handleFinish}>
              Finish
            </Button>
          ) : (
            <Button variant="contained" color="primary" onClick={handleNext}>
              Next
            </Button>
          )}
        </Box>
      </Container>
    </>
  );
};

export default CreateProject;
